import { fetch, HttpError } from '@tomra/client-side-http-client';
import type { components, operations } from '../api-types';
import { API_HOST } from '$lib/env';
import type { MaintenanceWindow } from '$lib/types/maintenanceWindow';
import {
  type ApiSoftwareRelease,
  mapApiSoftwareReleasesToSoftwareReleases
} from '$lib/mappers/softwareReleases';
import type { SoftwareRelease } from '$lib/types/softwareRelease';

import { authentication } from '$lib/authentication';
import { mapApiScopeToDomainScope } from '$lib/mappers/installationScopeApi';
import type { ScopeNodeCountry, ScopeSearchTreeNode } from '$lib/types/installationScopeTree';
import { extractPathsFromScopeTree } from '$lib/utils/scopeSearchTree';
import { appendMachineFamilyFilterParams } from '$lib/components/manifest/machineFamilyFilters';
import type { MachineFamilyFilter } from '$lib/components/manifest/machineFamilyFilters';
import { createSingleFlightCache } from '$lib/utils/singleFlightCache';
import type { JobFilters, InstallationJobDayFilters } from '$lib/types/jobFilters';
import type { SoftwareRolloutSortBy, SoftwareRolloutSortOrder } from '$lib/types/SoftwareRollout';
import { toApiMaintenanceWindow } from '$lib/mappers/maintenanceWindow';
import {
  mapScopePreview,
  mapScopePreviewDetails,
  type ScopePreview,
  type ScopePreviewDetails,
  type ScopeEntityType,
  type ScopePreviewChange
} from '$lib/types/scopePreview';
import type {
  ApiInstallationJobAttempt,
  ApiInstallationJobDayPage
} from '$lib/mappers/installationJob';

type InstallationDetails = components['schemas']['InstallationDetails'];
type InstallationOverviews = components['schemas']['InstallationOverviews'];
type InstallationStatistics = components['schemas']['InstallationStatistics'];
type ManifestOverviews = components['schemas']['ManifestOverviews'];
type ManifestDetails = components['schemas']['ManifestDetails'];
type ManifestCreateRequest = components['schemas']['ManifestCreateRequest'];
type ManifestUpdateRequest = components['schemas']['ManifestUpdateRequest'];
type SoftwareModuleType = components['schemas']['SoftwareModuleType'];
type AuthCountries = components['schemas']['AuthCountries'];
type UpdateWithoutManifestRequest = components['schemas']['UpdateWithoutManifestRequest'];
export type FileUpdateRequest = components['schemas']['FileUpdateRequest'];
export type FileUpdateResponse = components['schemas']['FileUpdateResponse'];
export type SelectAllMatching = components['schemas']['SelectAllMatching'];
type FileBasedInstallations = components['schemas']['FileBasedInstallations'];
type FileBasedInstallationFilterOptions =
  components['schemas']['FileBasedInstallationFilterOptions'];

export type FileBasedInstallation = components['schemas']['FileBasedInstallation'];

export type FileBasedInstallationSortBy = NonNullable<
  operations['getFileBasedInstallations']['parameters']['query']
>['sortBy'];

export type InstallationSortBy = NonNullable<
  operations['getInstallations']['parameters']['query']
>['sortBy'];
export type SortOrder = NonNullable<
  operations['getInstallations']['parameters']['query']
>['sortOrder'];
type NodeChild = components['schemas']['NodeChild'];
type InstallationScopeTree = components['schemas']['InstallationScopeTree'];
type InstallationScopeFilter = components['schemas']['InstallationScopeFilter'];
type UpdateInstallationScopeTreeRequest =
  components['schemas']['UpdateInstallationScopeTreeRequest'];
type JobTransitionHistory = components['schemas']['JobTransitionHistory'];
type RsyncConfigEntry = components['schemas']['RsyncConfigEntry'];
type SoftwareRollouts = components['schemas']['SoftwareRollouts'];
type MachineFamilies = components['schemas']['MachineFamilies'];

const requestConfig = {
  fetchNewToken: () => authentication.fetchNewToken(),
  getToken: () => authentication.getToken(),
  timeoutInMs: 120000
};

const JSON_HEADERS = {
  'Content-Type': 'application/json'
};

// Singleton promise to ensure authentication is initialized only once
let authInitPromise: Promise<void> | null = null;

// Set when an auth failure triggers a full-page Keycloak redirect. The load
// layer uses this to distinguish "we're navigating away to sign in" (keep the
// boot loader up) from a genuine network/API failure (show the error UI).
let authRedirectInProgress = false;

/**
 * True when `ensureAuthenticated()` has kicked off a Keycloak login redirect.
 * Consumers should keep the boot loader up rather than show a retry error,
 * because the browser is about to navigate away.
 */
export function isAuthRedirectInProgress(): boolean {
  return authRedirectInProgress;
}

async function ensureAuthenticated() {
  if (!authInitPromise) {
    // A fresh auth attempt is starting; clear any stale redirect flag so a
    // retry can re-classify the outcome instead of being stuck on redirect.
    authRedirectInProgress = false;
    authInitPromise = authentication.init().catch((e) => {
      // Reset the promise on error so we can retry
      authInitPromise = null;
      // Signal that a full-page redirect is imminent so the load layer keeps
      // the boot loader up instead of rendering the error screen.
      authRedirectInProgress = true;
      // Redirect to Keycloak login (will navigate away from current page)
      authentication.login();
      // Re-throw to prevent fetch from proceeding with invalid token
      throw e;
    });
  }
  return authInitPromise;
}

async function authenticatedFetch(url: string, options?: RequestInit) {
  await ensureAuthenticated();

  const optionsWithAuth = {
    ...options,
    headers: {
      ...JSON_HEADERS,
      ...(options?.headers || {})
    }
  };
  return fetch(url, optionsWithAuth, requestConfig).run();
}

/**
 * A cancelable request. `run()` issues the request (once auth is ready) and
 * resolves the mapped body; `abort()` cancels the in-flight request via the
 * HTTP client's own AbortController (which rejects `run()` with an
 * AbortError-named error — see `isTimeoutError`). Use for requests that can be
 * superseded (e.g. type-ahead search) so stale work is cancelled rather than
 * merely ignored.
 */
export interface Abortable<T> {
  run: () => Promise<T>;
  abort: () => void;
}

/**
 * Like `authenticatedFetch`, but returns the abortable handle instead of
 * eagerly running it. Auth is still resolved lazily inside `run()`; the
 * underlying request (and thus a meaningful `abort()`) only exists once auth
 * has settled and `run()` has been invoked.
 */
function abortableAuthenticatedFetch(url: string, options?: RequestInit): Abortable<unknown> {
  let handle: { abort: () => void } | null = null;
  let aborted = false;

  return {
    abort: () => {
      aborted = true;
      handle?.abort();
    },
    run: async () => {
      await ensureAuthenticated();
      const optionsWithAuth = {
        ...options,
        headers: {
          ...JSON_HEADERS,
          ...(options?.headers || {})
        }
      };
      const fetchHandle = fetch(url, optionsWithAuth, requestConfig);
      handle = fetchHandle;
      // Honour an abort that arrived while auth was still resolving.
      if (aborted) fetchHandle.abort();
      return fetchHandle.run();
    }
  };
}

// Cache the countries request so the root +layout.ts load and the root +page.ts
// redirect share a single call on cold start instead of fetching twice. The
// cache clears on error so a subsequent call can retry.
const countriesCache = createSingleFlightCache<AuthCountries>(() =>
  authenticatedFetch(`${API_HOST}/bff/auth/countries`, { method: 'GET' }).then((response) => {
    return (response.data as AuthCountries).sort((a, b) => a.localeCompare(b));
  })
);

export const getCountries = async (): Promise<AuthCountries> => {
  return countriesCache.get();
};

export const getInstallations = async (
  country: string,
  pageNumber: number = 1,
  pageSize: number = 20,
  serialNumber?: string,
  manifestIds?: string[],
  installationStates?: string[],
  softwareModuleTypes?: string[],
  latestJobStates?: string[],
  sortBy?: InstallationSortBy,
  sortOrder?: SortOrder
): Promise<InstallationOverviews> => {
  const params = new URLSearchParams({
    country: country,
    pageNumber: pageNumber.toString(),
    pageSize: pageSize.toString()
  });

  if (serialNumber) {
    params.append('serialNumber', serialNumber);
  }

  if (manifestIds && manifestIds.length > 0) {
    manifestIds.forEach((id) => params.append('manifestIds', id));
  }

  if (installationStates && installationStates.length > 0) {
    installationStates.forEach((state) => params.append('installationStates', state));
  }

  if (softwareModuleTypes && softwareModuleTypes.length > 0) {
    softwareModuleTypes.forEach((type) => params.append('softwareModuleTypes', type));
  }

  if (latestJobStates && latestJobStates.length > 0) {
    latestJobStates.forEach((state) => params.append('latestJobStates', state));
  }

  if (sortBy) {
    params.set('sortBy', sortBy);
  }
  if (sortOrder) {
    params.set('sortOrder', sortOrder);
  }

  return authenticatedFetch(`${API_HOST}/bff/installations?${params.toString()}`, {
    method: 'GET'
  }).then((response) => {
    return response.data as InstallationOverviews;
  });
};

export const getManifests = async (
  country: string,
  sortBy?: string,
  sortOrder?: string,
  pageNumber?: number,
  pageSize?: number,
  manifestName?: string
): Promise<ManifestOverviews> => {
  const params = new URLSearchParams({ country });

  if (sortBy) {
    params.set('sortBy', sortBy);
  }
  if (sortOrder) {
    params.set('sortOrder', sortOrder);
  }
  if (pageNumber !== undefined) {
    params.set('pageNumber', pageNumber.toString());
  }
  if (pageSize !== undefined) {
    params.set('pageSize', pageSize.toString());
  }
  if (manifestName) {
    params.set('manifestName', manifestName);
  }

  return authenticatedFetch(`${API_HOST}/bff/manifests?${params.toString()}`, {
    method: 'GET'
  }).then((response) => {
    return response.data as ManifestOverviews;
  });
};

export const getSoftwareReleases = async (
  softwareModuleType: string
): Promise<SoftwareRelease[]> => {
  const params = new URLSearchParams({ 'software-module-type': softwareModuleType });
  return authenticatedFetch(`${API_HOST}/bff/software-releases?${params.toString()}`, {
    method: 'GET'
  }).then((response) => {
    return mapApiSoftwareReleasesToSoftwareReleases(response.data as ApiSoftwareRelease[]);
  });
};

export type UpdateFileSource = 'RSYNC' | 'FILE' | 'RAUC';

export type ModuleTypeCategory = 'MAIN' | 'SUBMODULE';

export const getSoftwareModuleTypes = async (
  updateFileSource?: UpdateFileSource,
  category?: ModuleTypeCategory
): Promise<SoftwareModuleType[]> => {
  const params = new URLSearchParams();
  if (updateFileSource) {
    params.set('update-file-source', updateFileSource);
  }
  if (category) {
    params.set('category', category);
  }
  const query = params.toString();
  const url = `${API_HOST}/bff/software-module-types${query ? `?${query}` : ''}`;
  return authenticatedFetch(url, {
    method: 'GET'
  }).then((response) => {
    return response.data as SoftwareModuleType[];
  });
};

export const getInstallationById = async (id: string): Promise<InstallationDetails> => {
  return authenticatedFetch(`${API_HOST}/bff/installations/${id}`, { method: 'GET' }).then(
    (response) => {
      return response.data as InstallationDetails;
    }
  );
};

export const updateInstallation = async (
  installationId: string
): Promise<ApiInstallationJobAttempt> => {
  return authenticatedFetch(`${API_HOST}/bff/installations/${installationId}/update`, {
    method: 'POST'
  }).then((response) => {
    return response.data as ApiInstallationJobAttempt;
  });
};

export const updateInstallationWithoutManifest = async (
  installationId: string,
  request: UpdateWithoutManifestRequest
): Promise<ApiInstallationJobAttempt> => {
  return authenticatedFetch(
    `${API_HOST}/bff/installations/${installationId}/update-without-manifest`,
    {
      method: 'POST',
      body: JSON.stringify(request)
    }
  ).then((response) => {
    return response.data as ApiInstallationJobAttempt;
  });
};

export const getManifestById = async (id: string): Promise<ManifestDetails> => {
  return authenticatedFetch(`${API_HOST}/bff/manifests/${id}`, { method: 'GET' }).then(
    (response) => {
      return response.data as ManifestDetails;
    }
  );
};

export const createManifest = async (request: ManifestCreateRequest) => {
  return authenticatedFetch(`${API_HOST}/bff/manifests`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(request)
  });
};

export const getManifestScopeTree = async (
  id: string,
  filters: MachineFamilyFilter[] = [],
  onlyMatchingInstallations = false
): Promise<ScopeNodeCountry> => {
  const params = new URLSearchParams();
  appendMachineFamilyFilterParams(params, filters);
  if (onlyMatchingInstallations) params.set('onlyMatchingInstallations', 'true');
  const query = params.toString();
  const url = `${API_HOST}/bff/manifests/${id}/scope-tree${query ? `?${query}` : ''}`;
  return authenticatedFetch(url, { method: 'GET' }).then((response) => {
    return mapApiScopeToDomainScope(response.data as InstallationScopeTree);
  });
};

export const getManifestRsyncConfiguration = async (
  manifestId: string
): Promise<RsyncConfigEntry> => {
  return authenticatedFetch(`${API_HOST}/bff/manifests/${manifestId}/rsync-configuration`, {
    method: 'GET'
  }).then((response) => {
    return response.data as RsyncConfigEntry;
  });
};

export const updateManifestRsyncConfiguration = async (
  manifestId: string,
  rsyncVirtualConfigId: string
): Promise<void> => {
  const params = new URLSearchParams({ rsyncVirtualConfigId });
  await authenticatedFetch(
    `${API_HOST}/bff/manifests/${manifestId}/rsync-configuration?${params.toString()}`,
    { method: 'PUT' }
  );
};

export const updateManifestScopeTree = async (
  manifestId: string,
  scopeTree: InstallationScopeTree,
  filters: InstallationScopeFilter[] = []
): Promise<ScopeNodeCountry> => {
  const payload: UpdateInstallationScopeTreeRequest = {
    scope: scopeTree,
    filters
  };

  return authenticatedFetch(`${API_HOST}/bff/manifests/${manifestId}/scope-tree`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  }).then((response) => {
    return mapApiScopeToDomainScope(response.data as InstallationScopeTree);
  });
};

export const previewCreateManifestScopeTree = (
  countryCode: string,
  scopeTree: InstallationScopeTree,
  filters: InstallationScopeFilter[] = []
): Abortable<ScopePreview> => {
  const payload: components['schemas']['CreateInstallationScopePreviewRequest'] = {
    countryCode,
    scope: scopeTree,
    filters
  };
  const request = abortableAuthenticatedFetch(`${API_HOST}/bff/manifests/scope-tree/preview`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
  return {
    run: async () => mapScopePreview(await request.run()),
    abort: request.abort
  };
};

export const previewManifestScopeTree = (
  manifestId: string,
  scopeTree: InstallationScopeTree,
  filters: MachineFamilyFilter[] = []
): Abortable<ScopePreview> => {
  const payload = {
    scope: scopeTree,
    filters
  };

  const request = abortableAuthenticatedFetch(
    `${API_HOST}/bff/manifests/${manifestId}/scope-tree/preview`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  );
  return {
    run: async () => mapScopePreview(await request.run()),
    abort: request.abort
  };
};

export const previewManifestScopeDetails = (
  context: { mode: 'create'; countryCode: string } | { mode: 'edit'; manifestId: string },
  scope: InstallationScopeTree,
  filters: InstallationScopeFilter[],
  entityType: ScopeEntityType,
  change: ScopePreviewChange
): Abortable<ScopePreviewDetails> => {
  const params = new URLSearchParams({ entityType, change });
  const path = context.mode === 'create' ? '' : `/${context.manifestId}`;
  const payload =
    context.mode === 'create'
      ? { countryCode: context.countryCode, scope, filters }
      : { scope, filters };
  const request = abortableAuthenticatedFetch(
    `${API_HOST}/bff/manifests${path}/scope-tree/preview/details?${params.toString()}`,
    { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(payload) }
  );
  return {
    run: async () => mapScopePreviewDetails(await request.run()),
    abort: request.abort
  };
};

export const deleteManifest = async (manifestId: string): Promise<void> => {
  await authenticatedFetch(`${API_HOST}/bff/manifests/${manifestId}`, {
    method: 'DELETE'
  });
};

export const updateManifest = async (
  manifestId: string,
  manifestRequest: ManifestUpdateRequest
): Promise<ManifestDetails> => {
  return authenticatedFetch(`${API_HOST}/bff/manifests/${manifestId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(manifestRequest)
  }).then((response) => {
    return response.data as ManifestDetails;
  });
};

export const updateMaintenanceWindow = async (manifestId: string, data: MaintenanceWindow) => {
  return authenticatedFetch(`${API_HOST}/bff/manifests/${manifestId}/maintenance-window`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(toApiMaintenanceWindow(data))
  });
};

/**
 * Fetches child nodes from the installation scope tree API.
 *
 * The path segments build the hierarchical URL:
 * - [country]                                      → GET /bff/tree/{country}
 * - [country, chainId]                             → GET /bff/tree/{country}/chains/{chainId}
 * - [country, chainId, subChainId]                 → GET /bff/tree/{country}/chains/{chainId}/subchains/{subChainId}/regions
 * - [country, chainId, subChainId, region]         → GET /bff/tree/{country}/chains/{chainId}/subchains/{subChainId}/regions/{region}
 * - [country, chainId, subChainId, region, locId]  → GET /bff/tree/{country}/chains/{chainId}/subchains/{subChainId}/regions/{region}/locations/{locId}
 */
export const getTreeNodes = async (
  pathSegments: string[],
  filters: MachineFamilyFilter[] = [],
  onlyMatchingInstallations = false
): Promise<NodeChild[]> => {
  const [country, chainId, subChainId, region, locationId] = pathSegments;

  if (!country) throw new Error('At least a country is required to fetch tree nodes');

  const params = new URLSearchParams();
  if (chainId) params.set('chain_id', chainId);
  if (subChainId) params.set('sub_chain_id', subChainId);
  if (region) params.set('region', region);
  if (locationId) params.set('location_id', locationId);
  appendMachineFamilyFilterParams(params, filters);
  if (onlyMatchingInstallations) params.set('onlyMatchingInstallations', 'true');

  const query = params.toString();
  const url = `${API_HOST}/bff/tree/${country}${query ? `?${query}` : ''}`;

  return authenticatedFetch(url, { method: 'GET' }).then((response) => {
    return response.data as NodeChild[];
  });
};

export const getInstallationStatistics = async (
  country: string
): Promise<InstallationStatistics> => {
  return authenticatedFetch(`${API_HOST}/bff/statistics/installations?country=${country}`, {
    method: 'GET'
  }).then((response) => {
    return response.data as InstallationStatistics;
  });
};

export const getSoftwareRollouts = async (
  country: string,
  filters?: JobFilters & {
    sortBy?: SoftwareRolloutSortBy;
    sortOrder?: SoftwareRolloutSortOrder;
  }
): Promise<SoftwareRollouts> => {
  const params = new URLSearchParams({ country });

  if (filters?.sortBy) params.set('sortBy', filters.sortBy);
  if (filters?.sortOrder) params.set('sortOrder', filters.sortOrder);

  if (filters?.pageNumber !== undefined) {
    params.set('pageNumber', filters.pageNumber.toString());
  }
  if (filters?.pageSize !== undefined) {
    params.set('pageSize', filters.pageSize.toString());
  }
  if (filters?.manifestId) {
    params.set('manifestId', filters.manifestId);
  }
  if (filters?.manifestName) {
    params.set('manifestName', filters.manifestName);
  }
  if (filters?.fromDate) {
    params.set('fromDate', filters.fromDate);
  }
  if (filters?.toDate) {
    params.set('toDate', filters.toDate);
  }
  if (filters?.softwareModuleTypes && filters.softwareModuleTypes.length > 0) {
    filters.softwareModuleTypes.forEach((type) => params.append('softwareModuleTypes', type));
  }

  return authenticatedFetch(`${API_HOST}/bff/software-rollouts?${params.toString()}`, {
    method: 'GET'
  }).then((response) => {
    return response.data as SoftwareRollouts;
  });
};

export const getInstallationJobDays = async (
  country: string,
  filters?: InstallationJobDayFilters
): Promise<ApiInstallationJobDayPage> => {
  const params = new URLSearchParams({ country });

  if (filters?.pageNumber !== undefined) {
    params.set('pageNumber', filters.pageNumber.toString());
  }
  if (filters?.pageSize !== undefined) {
    params.set('pageSize', filters.pageSize.toString());
  }
  if (filters?.manifestId) {
    params.set('manifestId', filters.manifestId);
  }
  if (filters?.manifestName) {
    params.set('manifestName', filters.manifestName);
  }
  if (filters?.fromDate) {
    params.set('fromDate', filters.fromDate);
  }
  if (filters?.toDate) {
    params.set('toDate', filters.toDate);
  }
  if (filters?.softwareModuleTypes && filters.softwareModuleTypes.length > 0) {
    filters.softwareModuleTypes.forEach((type) => params.append('softwareModuleTypes', type));
  }
  if (filters?.serialNumber) {
    params.set('serialNumber', filters.serialNumber);
  }
  if (filters?.jobStates && filters.jobStates.length > 0) {
    filters.jobStates.forEach((state) => params.append('jobStates', state));
  }

  return authenticatedFetch(`${API_HOST}/bff/jobs/installation-days?${params.toString()}`, {
    method: 'GET'
  }).then((response) => {
    return response.data as ApiInstallationJobDayPage;
  });
};

export const getJobTransitionHistory = async (jobId: string): Promise<JobTransitionHistory> => {
  return authenticatedFetch(`${API_HOST}/bff/jobs/${jobId}/transitions`, {
    method: 'GET'
  }).then((response) => {
    return response.data as JobTransitionHistory;
  });
};

export const cancelJob = async (jobId: string): Promise<'cancelled' | 'already-final'> => {
  try {
    await authenticatedFetch(`${API_HOST}/bff/jobs/${jobId}`, {
      method: 'DELETE'
    });
    return 'cancelled';
  } catch (e) {
    // The job reached a terminal state between load and cancel — an expected race,
    // not an error. Report it as a no-op success so the UI can refresh gracefully.
    if (e instanceof HttpError && e.status === 409) {
      return 'already-final';
    }
    throw e;
  }
};

export const getJobsForInstallation = async (
  installationId: string,
  date: string
): Promise<ApiInstallationJobAttempt[]> => {
  const params = new URLSearchParams({ date });
  return authenticatedFetch(
    `${API_HOST}/bff/jobs/installations/${installationId}?${params.toString()}`,
    { method: 'GET' }
  ).then((response) => {
    return response.data as ApiInstallationJobAttempt[];
  });
};

export const getJobById = async (jobId: string): Promise<ApiInstallationJobAttempt> => {
  return authenticatedFetch(`${API_HOST}/bff/jobs/${jobId}`, { method: 'GET' }).then((response) => {
    return response.data as ApiInstallationJobAttempt;
  });
};

export const getRsyncConfigurations = async (): Promise<RsyncConfigEntry[]> => {
  return authenticatedFetch(`${API_HOST}/bff/rsync-configurations`, {
    method: 'GET'
  }).then((response) => {
    return response.data as RsyncConfigEntry[];
  });
};

export const getMachineFamilies = async (country: string): Promise<string[]> => {
  const params = new URLSearchParams({ country });
  return authenticatedFetch(`${API_HOST}/bff/machine-families?${params.toString()}`, {
    method: 'GET'
  }).then((response) => {
    return (response.data as MachineFamilies).machineFamilies;
  });
};

export interface ScopeTreeSearchResult {
  paths: string[][];
  tree: ScopeNodeCountry | null;
}

export const searchManifestScopeTree = (
  country: string,
  searchText: string,
  filters: MachineFamilyFilter[] = []
): Abortable<ScopeTreeSearchResult> => {
  const params = new URLSearchParams({ country, searchText });
  appendMachineFamilyFilterParams(params, filters);
  const handle = abortableAuthenticatedFetch(
    `${API_HOST}/bff/manifests/scope-tree/search?${params.toString()}`,
    { method: 'GET' }
  );
  return {
    abort: handle.abort,
    run: () =>
      handle.run().then((response) => {
        const raw: unknown = (response as { data: unknown }).data;
        if (Array.isArray(raw)) {
          return { paths: raw.filter((p): p is string[] => Array.isArray(p)), tree: null };
        }
        if (raw && typeof raw === 'object' && 'paths' in raw) {
          const arr = (raw as { paths: unknown }).paths;
          if (Array.isArray(arr)) {
            return { paths: arr.filter((p): p is string[] => Array.isArray(p)), tree: null };
          }
        }
        if (raw && typeof raw === 'object' && 'id' in raw) {
          const apiTree = raw as components['schemas']['InstallationScopeTree'];
          return {
            paths: extractPathsFromScopeTree(raw as ScopeSearchTreeNode),
            tree: mapApiScopeToDomainScope(apiTree)
          };
        }
        return { paths: [], tree: null };
      })
  };
};

export const createFileUpdate = async (request: FileUpdateRequest): Promise<FileUpdateResponse> => {
  return authenticatedFetch(`${API_HOST}/bff/file-based-updates`, {
    method: 'POST',
    body: JSON.stringify(request)
  }).then((response) => {
    return response.data as FileUpdateResponse;
  });
};

export const getFileBasedInstallations = async (
  country: string,
  softwareModuleType: string,
  pageNumber: number = 1,
  pageSize: number = 20,
  serialNumber?: string,
  chains?: string[],
  regions?: string[],
  versions?: string[],
  machineFamilies?: string[],
  sortBy?: FileBasedInstallationSortBy,
  sortOrder?: SortOrder
): Promise<FileBasedInstallations> => {
  const params = new URLSearchParams({
    country,
    softwareModuleType,
    pageNumber: pageNumber.toString(),
    pageSize: pageSize.toString()
  });

  if (serialNumber) {
    params.append('serialNumber', serialNumber);
  }
  if (chains && chains.length > 0) {
    chains.forEach((chain) => params.append('chains', chain));
  }
  if (regions && regions.length > 0) {
    regions.forEach((region) => params.append('regions', region));
  }
  if (versions && versions.length > 0) {
    versions.forEach((version) => params.append('versions', version));
  }
  if (machineFamilies && machineFamilies.length > 0) {
    machineFamilies.forEach((machineFamily) => params.append('machineFamilies', machineFamily));
  }
  if (sortBy) {
    params.set('sortBy', sortBy);
  }
  if (sortOrder) {
    params.set('sortOrder', sortOrder);
  }

  return authenticatedFetch(
    `${API_HOST}/bff/file-based-updates/installations?${params.toString()}`,
    {
      method: 'GET'
    }
  ).then((response) => {
    return response.data as FileBasedInstallations;
  });
};

export const getFileBasedInstallationFilterOptions = async (
  country: string,
  softwareModuleType: string
): Promise<FileBasedInstallationFilterOptions> => {
  const params = new URLSearchParams({ country, softwareModuleType });
  return authenticatedFetch(
    `${API_HOST}/bff/file-based-updates/filter-options?${params.toString()}`,
    {
      method: 'GET'
    }
  ).then((response) => {
    return response.data as FileBasedInstallationFilterOptions;
  });
};
